// The admin console's form logic, with no React in it.
//
// Components have no test coverage in this repository — there is no DOM harness,
// and this task does not add one — so everything decidable without a DOM is
// decided here and tested under node --test, the shape
// web/src/components/tickets/queue-query.js established.
//
// TWO VOCABULARIES, ONE RENDERER. An admin descriptor describes a field as
// `{ type, control }` (src/routes/api/admin/descriptors.js); the ticket create
// screen's `renderInput` switches on `data_type`, the vocabulary of the
// `custom_field_defs` table. They are the same idea named twice. `toRenderField`
// below is the single adapter between them, and it translates descriptor →
// renderer rather than the other way round: `data_type` values are rows in a
// database on a live system, so they cannot be renamed, while a descriptor is a
// literal in a source file. One renderer means a fix to focus rings or dark-mode
// contrast lands on both screens at once, which a near-copy would not.

import { ROLE_RANK } from '../../session/roles.js';
import { sortLabel } from './view-conditions.js';

/** Reasonable empty for each descriptor field type, so no control is uncontrolled. */
function emptyFor(field) {
  if (field.type === 'boolean') return false;
  if (field.type === 'array') return [];
  // `''` for an OBJECT field would reach the conditions builder as a string, and
  // `builderAvailable` would then send a brand new view to the JSON textarea. The
  // `saved-views` descriptor declares `default: { all: [] }` so a create never gets
  // here at all; this is the floor under that, and it is still an empty group
  // rather than `{}` — `compileConditions` throws on an object with neither key.
  if (field.type === 'object') return { all: [] };
  return '';
}

/**
 * The editable half of a row, keyed by the descriptor's own field names.
 *
 * Read-only columns (`id`, `updated_at`, `owner_user_id`) are not in `fields`, so
 * they are dropped here — sending one back is 422 UNKNOWN_FIELD, because
 * `validateBody` rejects any key the schema does not declare.
 *
 * @param {{fields: Record<string, object>}} descriptor
 * @param {object|null} row  null when creating.
 */
export function initialValues(descriptor, row) {
  const values = {};
  const creating = row === null || row === undefined;
  for (const [key, field] of Object.entries(descriptor.fields)) {
    const current = creating ? undefined : row[key];
    if (current !== undefined && current !== null) {
      values[key] = current;
      continue;
    }
    // A declared `default` is what the server would write if the key were absent
    // (src/lib/validate.js), so a CREATE form starts there rather than at the
    // type's empty. Without this, a boolean column whose DDL says `DEFAULT 1` is
    // created as 0 by this form and only by this form: `macros.is_active` and
    // `macros.is_shared` both default to 1, and a new macro that silently arrives
    // deactivated and unshared is one nobody can find afterwards.
    //
    // CREATE only. On an edit, an absent value is an absent value — applying a
    // default there would re-assert it on a row that had been deliberately
    // cleared, which is "key absent = unchanged" inverted.
    values[key] = creating && field.default !== undefined ? field.default : emptyFor(field);
  }
  return values;
}

/** Structural equality, enough for the scalars and small arrays a descriptor holds. */
function same(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
  if (a !== null && typeof a === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
}

/**
 * Only the keys that actually changed — which is what makes PATCH mean what the
 * contract says it means: "key absent = unchanged, key present with null =
 * cleared" (docs/API.md §3.4).
 *
 * A form that posted every field on every save would turn an edit of one field
 * into an overwrite of all of them, and every `config_events` before/after diff
 * would then show changes nobody made — the false signal the audit table exists
 * to avoid. It also matters mechanically here: the PATCH handler builds its SET
 * clause from the keys present, so a full-body PATCH rewrites `actions_json` on
 * every save of a macro whose actions were never touched.
 *
 * On CREATE (`row === null`) there is no "unchanged", so everything is sent and
 * the server's own `required` rules decide what is missing.
 */
export function changedValues(descriptor, values, row) {
  if (row === null || row === undefined) return { ...values };
  const out = {};
  for (const key of Object.keys(descriptor.fields)) {
    const was = row[key] ?? emptyFor(descriptor.fields[key]);
    if (!same(values[key], was)) out[key] = values[key];
  }
  return out;
}

/**
 * The fields the console FORM renders — everything except those the descriptor marks
 * `control: 'none'`.
 *
 * `control: 'none'` means "this column is real, the API writes it, and this console has
 * no control that can show it". It is the descriptor-level version of the
 * `value.authorable` flag the condition vocabulary already uses for the same reason
 * (src/services/admin/views.js), and it exists because the default branch of
 * `toRenderField` is a single-line text input: binding one to an OBJECT renders
 * `[object Object]`, and typing in it replaces the object with a string. The field stays
 * in `descriptor.fields`, so the schema still accepts it from whatever screen DOES
 * author it — `saved_reports.definition` is written by /reports/builder.
 *
 * `initialValues` and `changedValues` still walk every field, hidden ones included. That
 * is what makes an edit of the NAME leave the hidden column alone: the form never calls
 * `setValue` on it, so it compares equal and is omitted from the PATCH entirely.
 */
export function formFields(descriptor) {
  return Object.entries(descriptor?.fields ?? {})
    .filter(([, field]) => field?.control !== 'none');
}

/**
 * Whether this console can CREATE a row of this resource at all.
 *
 * False when a REQUIRED field is one the form cannot render: the create body would carry
 * `emptyFor`'s placeholder for it and the server would refuse. Decided mechanically from
 * the descriptor rather than from a list of resource names, so a descriptor added
 * tomorrow gets the right answer without anyone remembering this function.
 *
 * Presentation, never authorization — the same standing as the New button's rank check in
 * ResourceList: the server refuses the write regardless. The point is not to hand someone
 * a control whose only possible outcome is a 422.
 */
export function canCreateInConsole(descriptor) {
  return uncreatableFields(descriptor).length === 0;
}

/** The required-but-unrenderable fields, so a refusal can name them. */
function uncreatableFields(descriptor) {
  return Object.entries(descriptor?.fields ?? {})
    .filter(([, field]) => field?.required === true && field?.control === 'none');
}

/** Their labels, as one phrase: "Definition", or "Definition and Shape". */
export function uncreatableFieldLabels(descriptor) {
  const labels = uncreatableFields(descriptor).map(([key, field]) => field.label ?? key);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * A descriptor field, in the shape `renderInput` switches on.
 *
 * `control` wins over `type` because it is the more specific request: a textarea
 * still carries a string on the wire, and `actions` is still an array.
 */
export function toRenderField(key, field) {
  let dataType = 'text';
  if (field.control === 'textarea') dataType = 'textarea';
  else if (field.control === 'actions') dataType = 'actions';
  else if (field.control === 'conditions') dataType = 'conditions';
  else if (field.control === 'columns') dataType = 'columns';
  // `control: 'sort'` is still `type: 'enum'` on the wire and still validated
  // against SORT_VALUES server-side; the control only says that these particular
  // enum values are SORT KEYS, so `-updated_at` can be labelled rather than shown
  // raw. The labelling is mechanical (see `sortLabel`), so there is no second list.
  else if (field.control === 'sort') dataType = 'select';
  else if (field.type === 'boolean') dataType = 'boolean';
  else if (field.type === 'enum') dataType = 'select';
  else if (field.type === 'integer') dataType = 'number';

  return {
    key,
    // The key is a worse label than a real one and a much better label than
    // nothing — a blank <label> is a control nobody can name out loud.
    label: field.label ?? key,
    data_type: dataType,
    is_required: field.required === true,
    // A descriptor textarea is a macro body being written beside a keyword panel
    // nine rows tall; `renderInput`'s shared default of 3 leaves the control a
    // third the height of the thing next to it. Set here rather than in
    // `renderInput` so the ticket screen is untouched: custom field defs come out
    // of `custom_field_defs` and never carry `rows`, so they keep the default.
    rows: dataType === 'textarea' ? 10 : undefined,
    options: (field.values ?? []).map((value) => ({
      value, label: field.control === 'sort' ? sortLabel(value) : String(value),
    })),
  };
}

/**
 * Whether this descriptor's edit screen shows the macro keyword panel.
 *
 * Keyed on the descriptor name because the placeholder vocabulary is a property of
 * MACROS, not of textareas: `{{customer_name}}` means something to
 * `fillMacroTemplate` and nothing to any other descriptor's body of text. One
 * predicate, so the width and the panel can never disagree about which screen this
 * is.
 */
export function showsKeywordPanel(descriptor) {
  return descriptor?.name === 'macros';
}

/**
 * The width cap for a descriptor's edit form.
 *
 * `max-w-3xl` (768px) is the cap for every ordinary form — a 120rem-wide text input
 * is unusable. The macro screen puts an 18rem reference panel beside the form, and
 * at 768px that would leave the Body 464px, a 40% loss on the very control the
 * panel exists to help someone write. `max-w-5xl` (1024px) leaves it 720px, within
 * 48px of what it has today, and still fits the `max-w-7xl` band AppLayout gives
 * /admin at a 1280px viewport.
 *
 * Both class names are written out as complete literals so Tailwind's source
 * scanner still sees them — the rule AppLayout.jsx already follows.
 */
/**
 * The screens whose main control is wider than a text input.
 *
 * `macros` puts an 18rem reference panel beside the form; `saved-views` puts three
 * dropdowns and a checkbox list on ONE row, and at 768px a row that has to hold
 * field, operator and a multi-value picker wraps into an unreadable stack. Two
 * screens, two different reasons, one list — kept separate from
 * `showsKeywordPanel` because the keyword panel is a property of MACROS and the
 * width is a property of the control.
 */
const WIDE_SCREENS = new Set(['macros', 'saved-views']);

export function formWidthClass(descriptor) {
  return WIDE_SCREENS.has(descriptor?.name) ? 'max-w-5xl' : 'max-w-3xl';
}

/** One list cell. Never renders "null", "undefined", "true" or a raw ISO string. */
export function formatCell(row, columnKey) {
  const value = row?.[columnKey];
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleString();
  }
  if (Array.isArray(value)) return value.length === 0 ? '-' : String(value.length);
  return String(value);
}

/**
 * `details.fields` from a 422, split into what can be shown BESIDE a control and
 * what has to be shown ABOVE the form.
 *
 * Not every problem names a control, and this is the whole reason this function
 * exists rather than a one-line `mapped[f.field] = f.message`:
 *
 *   * `actions[0]` — src/services/admin/macros.js reports the INDEX, so the key
 *     never equals `actions` and a naive map files it under a key nothing renders.
 *   * `(body)` / `(resource)` — src/lib/validate.js and the admin router's own
 *     "Nothing to change." and delete refusal. Parenthesised on purpose: they are
 *     statements about the request, not about a field.
 *   * an UNKNOWN_FIELD key — a field name this descriptor does not declare.
 *
 * Anything with no control to sit beside goes to `formErrors` and is printed. The
 * alternative, which is what a naive mapper does, is a save that fails with an
 * empty explanation.
 */
export function mapFieldErrors(descriptor, problems) {
  const fieldErrors = {};
  const formErrors = [];

  for (const problem of problems ?? []) {
    const raw = String(problem?.field ?? '');
    // `actions[0]` and `actions[12]` are both the `actions` control.
    const base = raw.replace(/\[\d+\].*$/, '');
    const message = String(problem?.message ?? '').trim() || 'Not valid.';

    if (Object.hasOwn(descriptor.fields ?? {}, base)) {
      // The contract reports every problem at once (docs/API.md §1.6), so two
      // bad actions show two sentences rather than the last one silently winning.
      fieldErrors[base] = fieldErrors[base] ? `${fieldErrors[base]} ${message}` : message;
    } else if (raw.startsWith('(') || raw === '') {
      formErrors.push(message);
    } else {
      formErrors.push(`${raw}: ${message}`);
    }
  }

  return { fieldErrors, formErrors };
}

/**
 * What to show a user the console refused.
 *
 * FORBIDDEN and PRODUCT_NOT_LICENSED are distinct codes on the same 403 and the
 * remediation differs (src/lib/errors.js): FORBIDDEN means the rank held is below
 * what this resource requires, and the server's sentence already names the rank,
 * so it is passed through. PRODUCT_NOT_LICENSED means there is no
 * `user_product_roles` row for support at all — no rank change fixes it, only
 * group membership. Collapsing the two sends a licensed lead to ask for a licence
 * they already hold.
 */
export function consoleErrorMessage(error) {
  const code = error?.code;
  const message = typeof error?.message === 'string' && error.message.trim()
    ? error.message.trim()
    : null;

  if (code === 'PRODUCT_NOT_LICENSED') {
    return 'Your account is not licensed for support. Ask an administrator to add you to the right group.';
  }
  if (code === 'FORBIDDEN') {
    return message ?? 'Your role cannot administer this. Ask a support administrator.';
  }
  if (code === 'NOT_FOUND') return message ?? 'That record no longer exists.';
  return message ?? 'Something went wrong.';
}

/**
 * Whether to RENDER this descriptor's write affordances — never whether to permit
 * a write. `requireProductRole` plus the router's own per-descriptor `assertRank`
 * are the enforcement and refuse regardless of what this says (SPEC.md §7.3).
 *
 * Reads the DESCRIPTOR's own `requires`, not the router's `lead` floor: a
 * descriptor may raise its bar to `admin`, and /admin/:resource is a typeable URL.
 * Fails closed on an unknown held role AND on an unknown bar, matching
 * web/src/session/roles.js — a permissive fallback shows a Save whose only
 * possible outcome is a 403, which reads as a broken app rather than as a
 * boundary.
 *
 * @param {{requires?: string}} descriptor
 * @param {string|null} heldRole  from useProductRole('support').
 */
export function canAdminister(descriptor, heldRole) {
  const required = ROLE_RANK[descriptor?.requires];
  if (required === undefined) return false;
  const held = ROLE_RANK[heldRole];
  if (held === undefined) return false;
  return held >= required;
}

/**
 * The refusal banner: one headline, and the list under it.
 *
 * `mapFieldErrors` splits a 422 into problems that sit beside a control and
 * problems that do not. What is left is deciding what the banner SAYS, and the
 * obvious version double-prints: headline = `formErrors[0]`, list = `formErrors`,
 * so the first sentence appears twice. That was invisible while every refusal was
 * a single sentence — `tags` has exactly one — and `queues` is the first resource
 * that answers with three at once, all three of them `(resource)`.
 *
 *   a field problem   -> headline points at the controls; the list carries
 *                        anything that had no control to sit beside.
 *   exactly one       -> IT is the headline. A one-item bulleted list under a
 *                        heading that repeats it is not a list.
 *   more than one     -> a headline that says how to read the list, and the list.
 *
 * @param {Record<string, string>} fieldErrors  from mapFieldErrors
 * @param {string[]} formErrors                 from mapFieldErrors
 * @returns {{headline: string|null, list: string[]}}
 */
export function refusalBanner(fieldErrors, formErrors) {
  const unattached = formErrors ?? [];
  if (Object.keys(fieldErrors ?? {}).length > 0) {
    return { headline: 'Some fields need attention.', list: unattached };
  }
  if (unattached.length === 1) return { headline: unattached[0], list: [] };
  if (unattached.length > 1) {
    return { headline: 'This cannot be done yet, for more than one reason:', list: unattached };
  }
  return { headline: null, list: [] };
}

/**
 * The delete control's word and its confirm question.
 *
 * There are two deletes behind one button, and until `queues` there was only one,
 * so the button said "Deactivate" unconditionally. A descriptor now reports which
 * (`delete_mode`, src/routes/api/admin/descriptors.js `describeFor`):
 *
 *   'deactivate'  the handler sets `softDeleteColumn` to 0 and the row survives.
 *   'delete'      the table has no `is_active` column and the row is REMOVED,
 *                 taking its `queue_members` and `queue_rotation` rows with it
 *                 through the cascade.
 *
 * The word is not cosmetic. "Deactivate" on a row about to be destroyed is a
 * promise the handler does not keep, and the confirm dialog is the last place the
 * wrong word is still free to fix. Defaults to the soft wording, so a descriptor
 * that names no mode understates rather than overstates what the button does.
 *
 * @param {{delete_mode?: string}} descriptor
 * @param {string} singular  The resource's singular label, e.g. "Queue".
 * @returns {{label: string, confirm: string}}
 */
export function deletePrompt(descriptor, singular) {
  const noun = String(singular ?? 'record').trim().toLowerCase() || 'record';
  if (descriptor?.delete_mode === 'delete') {
    return {
      label: 'Delete',
      confirm: `Delete this ${noun} permanently? This cannot be undone.`,
    };
  }
  return { label: 'Deactivate', confirm: `Deactivate this ${noun}?` };
}

/**
 * The `actions` editor's text, read as a value.
 *
 * `control: 'actions'` is a JSON editor rather than a builder, and this is the
 * function that keeps that honest. The failure that matters is the SILENT one:
 * text the editor cannot read must block the save, never resolve to `[]`. A macro
 * whose actions round-trip to `[]` because a textarea could not parse them is
 * data loss with a success message on top of it.
 *
 * A non-array is refused as well. `actions_json`'s column CHECK is one-argument
 * `ISJSON`, which accepts `{}` — precisely the hole
 * src/services/admin/macros.js exists to close — so the editor must not be able
 * to author one either.
 *
 * @returns {{ok: true, value: unknown[], error: null}|{ok: false, error: string}}
 */
export function parseActionsDraft(text) {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return { ok: true, value: [], error: null };

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { ok: false, error: `Not valid JSON: ${err.message}` };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: 'Actions must be a JSON array of { "type": …, "value": … } objects.' };
  }
  return { ok: true, value: parsed, error: null };
}
